import Info from '@mdi/svg/svg/information.svg?react';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import head from 'lodash/head';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactSelect, { components } from 'react-select';
import Creatable from 'react-select/creatable';
import styled from 'styled-components';
import { EMPTY_OBJ } from '../../constants';
import {
  badgeSize,
  COLOR,
  fieldBorderRadius,
  fieldDisabledOpacity,
  fieldFocusShadow,
  fieldLabelFontSize,
  fieldLabelMargin,
  FONT_SIZE,
  SIZE,
} from '../../styles';
import { Button } from '../Button';
import { FieldInfo } from '../FieldInfo';
import { type FieldInfoType } from '../FieldInfo/FieldInfo.types';
import { Flex } from '../Flex';
import { Grid } from '../Grid';
import { MDIIcon } from '../MDIIcon';
import { type SelectOption, type SelectProps } from './Select.types';

const PLACEHOLDER_COLOR_DARK_MODE = '#CECECE';
const PLACEHOLDER_COLOR_LIGHT_MODE = '#A0A0A0';

const theme = {
  borderRadius: 4,
  colors: {
    danger: '#DE350B',
    dangerLight: '#FFBDAD',
    neutral0: 'hsl(0, 0%, 100%)',
    neutral10: 'hsl(0, 0%, 90%)',
    neutral20: 'hsl(0, 0%, 80%)',
    neutral30: 'hsl(0, 0%, 70%)',
    neutral40: 'hsl(0, 0%, 60%)',
    neutral5: 'hsl(0, 0%, 95%)',
    neutral50: 'hsl(0, 0%, 50%)',
    neutral60: 'hsl(0, 0%, 40%)',
    neutral70: 'hsl(0, 0%, 30%)',
    neutral80: 'hsl(0, 0%, 20%)',
    neutral90: 'hsl(0, 0%, 10%)',
    primary: '#2580bc',
    primary25: '#2580bc', //'#55b6f5',
    primary50: '#2580bc', //'#45a4e2',
    primary75: '#2580bc', //'#3592cf',
  },
  spacing: {
    baseUnit: 4,
    controlHeight: 36,
    menuGutter: 4,
  },
};

// eslint-disable-next-line
// @ts-ignore
const isDefault = (option: SelectOption, defaultValue) => option.value === defaultValue;

// eslint-disable-next-line
// @ts-ignore
const getPlaceholderStyler = (colorMode) => (base) => ({
  ...base,
  color: colorMode === 'dark' ? PLACEHOLDER_COLOR_DARK_MODE : PLACEHOLDER_COLOR_LIGHT_MODE,
});

// eslint-disable-next-line
// @ts-ignore
const getDefaultStyles = (defaultValue, colorMode) => {
  return {
    // eslint-disable-next-line
    // @ts-ignore
    clearIndicator: (base, state) =>
      // eslint-disable-next-line
      // @ts-ignore
      isDefault(head(state.getValue()), defaultValue) ? { display: 'none' } : base,
    // eslint-disable-next-line
    // @ts-ignore
    multiValue: (base, { data }) => {
      if (data.isFixed) {
        return { ...base, background: 'gray' };
      } else if (isDefault(data, defaultValue)) {
        return {
          ...base,
          background: 'none',
        };
      }
      return base;
    },

    // eslint-disable-next-line
    // @ts-ignore
    multiValueLabel: (base, { data }) => {
      if (data.isFixed) {
        return {
          ...base,
          fontWeight: 'bold',
          color: 'white',
          paddingRight: 6,
        };
      } else if (isDefault(data, defaultValue)) {
        return {
          ...base,
          color: '#888',
          cursor: 'pointer !important',
          fontFamily: 'inherit',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '18px',
          padding: 0,
          paddingLeft: 2,
          paddingRight: 5,
        };
      }
      return base;
    },

    // eslint-disable-next-line
    // @ts-ignore
    multiValueRemove: (base, { data }) =>
      data.isFixed || isDefault(data, defaultValue) ? { display: 'none' } : base,

    placeholder: getPlaceholderStyler(colorMode),
  };
};

export const Select: React.FC<SelectProps> = memo((props) => {
  const {
    autoFocus,
    canOpenMenu = true,
    changeByProp,
    className = 'select-single',
    classNamePrefix = 'select',
    colorMode = 'light',
    containerClassName = '',
    customIsValidNewOptionHandler,
    createLabel = 'Create',
    createLabelQuotes = '""',
    dataTestId = '',
    defaultValue,
    disabled,
    errorText,
    filterOption,
    formatGroupLabel: formatGroupLabelProp,
    hintText,
    infoIconTooltip,
    inline = false,
    isClearable = false,
    isDroppable = true,
    isEditable = false,
    isLoading = false,
    isMulti = false,
    isRtl = false,
    isSearchable = true,
    labelText,
    noOptionsMessage,
    layoutStyle = EMPTY_OBJ,
    menuHeight = 180,
    menuPlacement,
    name,
    onBlur,
    onChange,
    onValueChange,
    onFocus,
    onInputChange,
    inputValue,
    options,
    placeholder = '',
    refFunc,
    styles: styleOverrides = EMPTY_OBJ,
    successText,
    value: propsValue,
    valueObject: sourceValueObject,
    withInfoIcon,
    wrapClassName = '',
    isOptionDisabled,
    warningText,
  } = props;

  const selectRef = useRef<{ focus: () => void; blur: () => void } | null>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [styles, setStyles] = useState(
    merge(getDefaultStyles(defaultValue, colorMode), styleOverrides),
  );

  const setupReference = useCallback(() => {
    if (refFunc) {
      // eslint-disable-next-line
      // @ts-ignore
      refFunc(selectRef.current);
    }
  }, [refFunc]);

  useEffect(() => {
    setupReference();
    const verifiedValue = getVerifiedValue();
    if (propsValue !== verifiedValue) {
      setTimeout(() => applyValue(verifiedValue));
    }
    return () => {
      allowExternalKeyDown();
    };
  }, []);

  useEffect(() => {
    setStyles(merge(getDefaultStyles(defaultValue, colorMode), styleOverrides));
  }, [colorMode, styleOverrides, defaultValue]);

  const getVerifiedValue = ({ value = propsValue, getObject = false } = {} as any) => {
    if (isEmpty(value) && !isEmpty(defaultValue)) {
      const returnValue = getObject ? getValueOptions(defaultValue) : defaultValue;
      return isMulti ? [returnValue] : returnValue;
    }
    return value;
  };

  const orderOptions = (values: SelectOption[]) =>
    values.filter((value) => value.isFixed).concat(values.filter((value) => !value.isFixed));

  // eslint-disable-next-line
  // @ts-ignore
  const trapKeyDown = (event) => {
    // Trap ESC and ENTER when the user is selecting dropdown options
    if ([13, 27].includes(event.keyCode)) {
      event.skipModal = true;
    }
  };

  const preventExternalKeyDown = () => document.addEventListener('keydown', trapKeyDown, true);

  const allowExternalKeyDown = () => document.removeEventListener('keydown', trapKeyDown, true);

  // eslint-disable-next-line
  // @ts-ignore
  const onBlurHandler = (event) => {
    if (focus) {
      // Set the event input's name to the name of the hidden attribute that
      // actually holds the value (as per the `name` prop).
      const renamedEvent = merge({}, event, { target: { id: name } });

      // eslint-disable-next-line
      // @ts-ignore
      onBlur?.(renamedEvent);

      setFocus(false);
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const onFocusHandler = (event) => {
    if (!focus) {
      // eslint-disable-next-line
      // @ts-ignore
      onFocus?.(event);
      setFocus(true);
    }
  };

  const addHover = () => {
    if (!hover) {
      setHover(true);
    }
  };

  const removeHover = () => setHover(false);

  const focusOnSelect = () => selectRef.current?.focus();

  const onMenuClose = () => {
    if (isMenuOpen) {
      allowExternalKeyDown();
      setIsMenuOpen(false);
    }
  };

  const onMenuOpen = () => {
    if (!isMenuOpen) {
      preventExternalKeyDown();
      setIsMenuOpen(true);
    }
  };

  // eslint-disable-next-line
  // @ts-ignore
  const getValueOptions = (sourceValue) =>
    Array.isArray(sourceValue)
      ? sourceValue.map((current) => findOptionByValue(current))
      : findOptionByValue(sourceValue);

  // eslint-disable-next-line
  // @ts-ignore
  const findOptionByValue = (sourceValue) => {
    const sourceOptions = options;
    // eslint-disable-next-line
    // @ts-ignore
    let output;
    sourceOptions.forEach((option) => {
      const { value } = option;
      // eslint-disable-next-line
      // @ts-ignore
      if (!output) {
        if (typeof value !== 'undefined' && value === sourceValue) {
          output = option;
        } else if ('options' in option) {
          output = option.options.find(({ value }) => value === sourceValue);
        }
      }
    });
    return output;
  };

  // eslint-disable-next-line
  // @ts-ignore
  const getResultValue = (result) =>
    isArray(result) ? result.map((option) => option?.value) : result?.value;

  // eslint-disable-next-line
  // @ts-ignore
  const handleChange = (sourceResult, { action, option, removedValue }) => {
    const isRemovalAction = ['remove-value', 'pop-value', 'clear'].includes(action);
    const isAddAction = ['set-value', 'select-option'].includes(action);

    // cloneDeep is only needed when we mutate multi-value arrays (isFixed / isUnique).
    // Deep-cloning a selected option that contains React nodes is expensive on the click path.
    // For single-select, `result` (and `result.value`) is the same object reference as the
    // matching entry in `options` — do not mutate the value received in onChange in place.
    let result = isMulti ? cloneDeep(sourceResult) : sourceResult;

    // Modify result based on special properties, like `isFixed` and `isUnique`
    if (isMulti) {
      if (isAddAction) {
        if (option.isUnique) {
          result = [option];
        } else {
          // eslint-disable-next-line
          // @ts-ignore
          result = result.filter((value) => !value.isUnique);
        }
      } else if (isRemovalAction) {
        if (get(removedValue, 'isFixed')) {
          return;
        }
        if (action === 'clear') {
          result = options.filter((value) => value.isFixed);
        }
        result = getVerifiedValue({ value: result, getObject: true });
      } else {
        result = orderOptions(result);
      }
    }

    // Propagate the new result up
    const value = getResultValue(result);
    applyValue(value);
  };

  const applyValue = (value: any) => {
    onValueChange?.(value);
    if (changeByProp) {
      onChange?.(changeByProp, value);
    } else {
      onChange?.({ value });
    }
  };

  const getOption = () => {
    // eslint-disable-next-line
    // @ts-ignore
    const option = (props) => {
      const {
        data,
        data: { text, textList },
        innerProps: { onMouseOver },
        isFocused,
        isSelected,
        selectOption,
      } = props;

      const optionClass = classNames(
        'select-option',
        { focused: isFocused },
        { selected: isSelected },
      );

      const { label, tooltip, dataTestId } = data;
      const iconFill = isFocused ? 'var(--color-white)' : 'var(--color-primary)';
      const innerProps = dataTestId
        ? { ...props.innerProps, 'data-testid': dataTestId }
        : props.innerProps;

      return (
        <div className={optionClass} onMouseOver={onMouseOver}>
          <components.Option {...props} innerProps={innerProps}>
            <Flex alignItems="center">
              {label}
              {!!tooltip && (
                <Button
                  className="select-info"
                  icon={<MDIIcon icon={Info} className="icon-info" fill={iconFill} />}
                  type="clear"
                  htmlButtonType="button"
                  iconOnly
                  tooltip={tooltip}
                  colorMode="light"
                />
              )}
            </Flex>
          </components.Option>

          {text && (
            <span className="sub-text" onClick={() => selectOption(data)}>
              {text}
            </span>
          )}
          {textList &&
            // eslint-disable-next-line
            // @ts-ignore
            textList.map((textItem, index) => {
              return (
                <span
                  key={`text-item-${index}`}
                  className="sub-text sub-text-item"
                  onClick={() => selectOption(data)}
                >
                  {textItem}
                </span>
              );
            })}
        </div>
      );
    };
    option.displayName = 'Option';
    return option;
  };

  // eslint-disable-next-line
  // @ts-ignore
  const localizeCreation = (input) =>
    // eslint-disable-next-line
    // @ts-ignore
    `${createLabel} ${createLabelQuotes[0]}${input}${createLabelQuotes[1]}`;
  // eslint-disable-next-line
  // @ts-ignore
  const isValidNewOptionHandler = (value) => {
    const canOpenEditableMenu =
      !isEmpty(value) && !options.find((option) => option.value === value);
    return canOpenEditableMenu;
  };
  // eslint-disable-next-line
  // @ts-ignore
  const formatGroupLabel = (group) => {
    if (formatGroupLabelProp) return formatGroupLabelProp(group);
    const { label, options } = group;
    if (!label || isEmpty(label)) {
      return null;
    } else if (label.toLowerCase() === 'divider') {
      return <span className="divider" />;
    }
    return (
      <div className="group-label">
        <span>{label}</span>
        <span className="group-label-badge">{options.length}</span>
      </div>
    );
  };

  const fieldsetClass = classNames(
    'select-field',
    `mode-${colorMode}`,
    containerClassName,
    wrapClassName,
    {
      active: focus || hover,
      disabled,
      success: successText,
      warning: warningText,
      error: errorText,
      inline,
    },
  );
  const selectClass = classNames('select-control', className);

  const valueObject = sourceValueObject
    ? sourceValueObject
    : isNil(propsValue)
      ? getValueOptions(defaultValue)
      : getValueOptions(propsValue);
  const Option = getOption();
  const selectUIComponents = {
    Option,
    ...(isEditable && !isDroppable ? { DropdownIndicator: null } : {}),
  };

  const fieldInfo = getFieldInfo({ errorText, warningText, hintText, successText });

  return (
    <Wrapper
      className={fieldsetClass}
      onBlur={removeHover}
      onMouseLeave={removeHover}
      onMouseEnter={addHover}
      style={layoutStyle}
      data-testid={dataTestId ? `${dataTestId}-selector` : 'selector'}
      // eslint-disable-next-line
      // @ts-ignore
      withInfoIcon={withInfoIcon}
    >
      <Grid
        gridTemplateColumns={inline ? 'auto 1fr' : '100%'}
        gridTemplateRows="auto"
        gridColumnGap={inline ? '22px' : '0'}
      >
        {labelText && (
          <Label htmlFor={name} onClick={focusOnSelect}>
            {labelText}
            {withInfoIcon && (
              <Button
                className="select-info"
                icon={<MDIIcon icon={Info} className="icon-info" />}
                type="clear"
                htmlButtonType="button"
                iconOnly
                tooltip={infoIconTooltip}
                tooltipStyle={{ maxWidth: '250px' }}
              />
            )}
          </Label>
        )}
        {isEditable && (
          <Creatable
            /*
             * The key is necessary to programmatically clear the value
             * It will force a rerender when the value changes
             */
            key={`${name}-${propsValue}`}
            autoFocus={autoFocus}
            className={selectClass}
            classNamePrefix={classNamePrefix}
            components={selectUIComponents}
            data-testid={dataTestId}
            filterOption={filterOption}
            formatCreateLabel={localizeCreation}
            formatGroupLabel={formatGroupLabel}
            id={name}
            isClearable={isMulti || isClearable}
            isDisabled={disabled}
            isLoading={isLoading}
            isMulti={isMulti}
            isSearchable={isSearchable}
            isValidNewOption={
              customIsValidNewOptionHandler
                ? customIsValidNewOptionHandler
                : isValidNewOptionHandler
            }
            maxMenuHeight={menuHeight}
            menuIsOpen={canOpenMenu ? isMenuOpen : false}
            noOptionsMessage={noOptionsMessage ?? (() => null)}
            menuPlacement={menuPlacement}
            menuShouldScrollIntoView={true}
            name={name}
            onBlur={onBlurHandler}
            // eslint-disable-next-line
            // @ts-ignore
            onChange={handleChange}
            onFocus={onFocusHandler}
            onInputChange={onInputChange}
            inputValue={inputValue}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
            options={canOpenMenu ? options : []}
            placeholder={placeholder}
            ref={(el) => {
              selectRef.current = el;
            }}
            styles={styles}
            theme={theme}
            value={valueObject}
          />
        )}
        {!isEditable && (
          <ReactSelect
            /*
             * The key is necessary to programmatically clear the value
             * It will force a rerender when the value changes
             */
            key={`${name}-${propsValue}`}
            autoFocus={autoFocus}
            className={selectClass}
            classNamePrefix={classNamePrefix}
            components={selectUIComponents}
            data-testid={dataTestId}
            filterOption={filterOption}
            formatGroupLabel={formatGroupLabel}
            id={name}
            isClearable={isMulti || isClearable}
            isDisabled={disabled}
            isLoading={isLoading}
            isMulti={isMulti}
            isRtl={isRtl}
            isSearchable={isSearchable}
            maxMenuHeight={menuHeight}
            menuIsOpen={canOpenMenu ? isMenuOpen : false}
            noOptionsMessage={noOptionsMessage ?? (() => null)}
            menuPlacement={menuPlacement}
            menuShouldScrollIntoView={true}
            name={name}
            onBlur={onBlurHandler}
            // eslint-disable-next-line
            // @ts-ignore
            onChange={handleChange}
            onFocus={onFocusHandler}
            onInputChange={onInputChange}
            inputValue={inputValue}
            onMenuClose={onMenuClose}
            onMenuOpen={onMenuOpen}
            options={options}
            placeholder={placeholder}
            ref={(el) => {
              selectRef.current = el;
            }}
            styles={styles}
            theme={theme}
            value={valueObject}
            isOptionDisabled={isOptionDisabled}
          />
        )}
        <div className="under-select-wrapper">{!!fieldInfo && <FieldInfo {...fieldInfo} />}</div>
      </Grid>
    </Wrapper>
  );
});

function getFieldInfo({
  errorText,
  warningText,
  hintText,
  successText,
}: {
  errorText?: string;
  warningText?: string;
  hintText?: string;
  successText?: string;
}): { type: FieldInfoType; text: string } | undefined {
  if (errorText) {
    return { type: 'error', text: errorText };
  }
  if (warningText) {
    return { type: 'warning', text: warningText };
  }
  if (successText) {
    return { type: 'success', text: successText };
  }
  if (hintText) {
    return { type: 'hint', text: hintText };
  }
  return undefined;
}

// Styled Components
const Wrapper = styled.fieldset<{ withInfoIcon: boolean }>`
  border: none;
  display: block;
  font-family: inherit;
  margin: 0 0 ${SIZE['20']};
  padding: 0;

  fieldset + &:last-child {
    margin-bottom: 0;
  }

  &.disabled {
    opacity: ${fieldDisabledOpacity};

    &,
    input,
    input:disabled,
    label {
      cursor: not-allowed;
    }

    .select__control {
      background: ${COLOR['fieldDisabledBG']};
    }
  }

  &.success {
    .select__control {
      border-color: ${COLOR['success']};
      box-shadow: ${COLOR['fieldSuccessShadow']};
    }

    label {
      color: ${COLOR['success']};
    }
  }

  &.warning {
    .select__control {
      border-color: ${COLOR['warning']};
      box-shadow: ${COLOR['fieldWarningShadow']};
    }

    label {
      color: ${COLOR['warning']};
    }
  }

  &.error {
    .select__control {
      border-color: ${COLOR['red-dark']};
      box-shadow: ${COLOR['fieldErrorShadow']};
    }

    label {
      color: ${COLOR['red-dark']};
    }
  }

  &.active:not(.disabled) .select__control,
  .select__control:hover,
  .select__control:focus,
  .select__control.select__control--is-focused,
  .select__control.select__control--menu-is-open {
    border-color: ${COLOR['primary']};
    box-shadow: ${fieldFocusShadow};
    outline: none;
  }

  &.active:not(.disabled) {
    label {
      color: ${COLOR['primary']};
    }
  }

  &,
  label {
    box-sizing: border-box;
    display: ${(p) => (p.withInfoIcon ? 'flex' : 'block')};
    align-items: ${(p) => (p.withInfoIcon ? 'center' : 'normal')};
    width: 100%;
  }

  &.mode-dark,
  &.mode-darker {
    + input-placeholder {
      color: ${COLOR['light-gray']};
    }

    &.active:not(.disabled) {
      label {
        color: ${COLOR['turquoise']};
      }
    }

    &.error {
      label {
        color: ${COLOR['red']};
      }
    }

    input,
    label,
    .select-option,
    .select__control,
    .select__menu {
      color: ${COLOR['white']};

      .sub-text {
        color: ${COLOR['white']};
      }
    }

    .select__input {
      color: ${COLOR['white']} !important;
    }

    .select__control,
    .select__menu {
      background: ${COLOR['dark-gray']};
    }

    .select__control {
      border-color: ${COLOR['dark-gray']};

      .select__single-value {
        color: ${COLOR['white']};
      }
    }

    .select__dropdown-indicator {
      svg {
        fill: ${COLOR['white']};
      }
    }

    .select__multi-value {
      background: ${COLOR['turquoise-dark']};

      .select__multi-value__label {
        color: ${COLOR['white']};
      }
    }

    .select__clear-indicator {
      svg {
        fill: ${COLOR['light-gray-darkest']};
      }

      &:hover {
        svg {
          fill: ${COLOR['white']};
        }
      }
    }

    .select__multi-value__remove {
      svg {
        fill: ${COLOR['white']};
      }

      &:hover {
        svg {
          fill: ${COLOR['red-medium-light']};
        }
      }
    }
  }

  &.mode-darker {
    .select__control,
    .select__menu {
      background: ${COLOR['dark-gray-dark']};
    }

    .select__control {
      border-color: ${COLOR['dark-gray-dark']};
    }
  }

  &.inline {
    label {
      width: auto;
    }

    .under-select-wrapper {
      grid-column-start: 2;

      .field-legend {
        margin-top: 2px;
      }
    }
  }

  input {
    cursor: pointer;
  }

  .under-select-wrapper {
    width: 100%;
  }

  .select__value-container,
  .select__indicator {
    cursor: pointer;
  }

  .select__multi-value + div {
    padding-bottom: 0;
    padding-top: 0;

    &,
    input {
      color: ${COLOR['black']};
      font-size: ${FONT_SIZE['14']};
      line-height: ${SIZE['16']};
    }
  }

  .select__multi-value__label {
    cursor: default;
  }

  .select-control {
    width: 100%;
    text-transform: capitalize;
  }

  .select__control,
  .select__menu {
    font-size: ${FONT_SIZE['14']};
    line-height: ${SIZE['16']};
  }

  .select__control {
    background: ${COLOR['white']};
    border-radius: ${fieldBorderRadius};
    border: 1px solid ${COLOR['fieldBorderColor']};
    font-size: ${FONT_SIZE['14']};
    line-height: ${SIZE['16']};

    .select__indicator {
      padding: 7px;
    }
  }

  .select__menu {
    z-index: 1000000000;

    .select__group {
      padding-bottom: 0;
      padding-top: 0;

      .select__group-heading {
        padding: ${SIZE['4']} ${SIZE['8']};

        &:empty {
          display: none;
        }
      }
    }
  }

  .select-option,
  .select__menu {
    color: ${COLOR['textColor']};
    font-weight: 400;

    &,
    [class*='option'] {
      cursor: pointer;
    }

    &.selected,
    [class*='--is-selected'] {
      background: ${COLOR['light-gray-light']};
      color: ${COLOR['dark-gray']};

      .sub-text {
        color: ${COLOR['dark-gray']};
      }
    }

    &.focused,
    [class*='--is-focused'] {
      background: ${COLOR['primary']};
      color: ${COLOR['white']};

      .sub-text {
        color: ${COLOR['white']};
      }
    }

    .select__option {
      padding: ${SIZE['4']} ${SIZE['8']};
    }
  }

  .select-option {
    padding: ${
      // eslint-disable-next-line
      // @ts-ignore
      SIZE['4 0']
    };
    overflow-wrap: break-word;

    .sub-text {
      color: ${COLOR['light-gray-darkest']};
      display: block;
      font-size: ${FONT_SIZE['12']};
      font-weight: 300;
      padding: ${SIZE['4']} ${SIZE['12']};
    }
  }

  .select__multi-value__remove {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .divider,
  .group-label {
    display: flex;
  }

  .group-label {
    align-items: center;
    color: ${COLOR['light-gray-darkest']};
    font-size: ${FONT_SIZE['14']};
    font-weight: 200;
    justify-content: space-between;
    padding: ${
        // eslint-disable-next-line
        // @ts-ignore
        SIZE['12 0']
      }
      ${SIZE['8']};

    .group-label-badge {
      background: ${COLOR['light-gray-light']};
      border-radius: ${badgeSize} * 2;
      color: ${COLOR['dark-gray']};
      display: inline-block;
      font-size: ${FONT_SIZE['12']};
      line-height: ${FONT_SIZE['14']};
      margin-left: ${SIZE['8']};
      min-height: ${badgeSize};
      min-width: ${badgeSize};
      padding: 1px 7px 0px;
      position: relative;
      vertical-align: baseline;
    }
  }

  .divider {
    background: ${COLOR['light-gray-light']};
    height: 1px;
    margin: 3px 0 0;
    padding: 0;
  }
`;

const Label = styled.label`
  color: ${COLOR['fieldLabelColor']};
  cursor: pointer;
  font-size: ${fieldLabelFontSize};
  line-height: ${SIZE['16']};
  margin: ${fieldLabelMargin};
  padding: 0;

  .select-info.clear {
    margin: 0;

    .icon-info {
      fill: ${COLOR['light-gray-darkest']};
      width: ${SIZE['16']};
      height: ${SIZE['16']};
      top: 0;
      margin: 0;
    }

    &:hover .icon-info {
      fill: ${COLOR['turquoise-dark']};
    }
  }
`;

// Display Name
Select.displayName = 'Select';

export default Select;
